import { useParams } from 'react-router-dom'
import { Box, Button, CircularProgress, Stack, useMediaQuery } from '@mui/material'
import { MainBox, CommentsButton, SideBox, StackStyle } from './style'
import { PureLightTheme } from '../../../styles/theme'
import { NotFound } from '../../../components/ui-kit/NotFound'
import { GrowthToolAccessRestrictedStub } from '../../../components/GrowthTool/GrowthToolAccessRestrictedStub'
import { CommentsWrapper } from '../../../components/GrowthTool/CommentsWrapper'
import { useEffect, useLayoutEffect, useState } from 'react'
import { CommentsDrawer } from '../../../components/ui-kit/CommentsDrawer'
import { IconComments } from '../../../icons/IconComments'
import { GrowthToolDescription } from '../../../components/GrowthTool/GrowthToolDescription'
import { GrowthToolInfo } from '../../../components/GrowthTool/GrowthToolInfo'
import { commentsButtonPwaSx } from '../../../consts/mobileLayout'
import { usePwaMobile } from '../../../hooks/usePwaMobile'
import { useGetGrowthToolQuery, useTrackGrowthToolViewMutation } from '../../../api/growthTools'
import { resolveGrowthToolCommentsTotal } from '../../../api/types/growthTools'
import { formatNumber } from '../../../common/FormatNumber'
import { extractApiErrorMessage, isGrowthToolAccessRestrictedError } from '../../../utils/api'

export const GrowthTool = () => {
  useLayoutEffect(() => {
    document.title = 'Инструмент личностного роста'
  }, [])

  const isTablet = useMediaQuery(PureLightTheme.breakpoints.down('md'))
  const isPwaMobile = usePwaMobile()
  const { id } = useParams<{ id: string }>()
  const cacheKey = id ?? ''
  const {
    data: growthTool,
    error: growthToolError,
    isLoading,
    isFetching,
    isError,
  } = useGetGrowthToolQuery(
    { id: cacheKey },
    { skip: !cacheKey, refetchOnMountOrArgChange: true },
  )
  const [trackGrowthToolView] = useTrackGrowthToolViewMutation()
  const [fetchSettled, setFetchSettled] = useState(false)

  const isAccessRestricted = isGrowthToolAccessRestrictedError(growthToolError)

  const [openComments, setOpenComments] = useState(false)
  const handleOpenComments = () => setOpenComments(true)
  const handleCloseComments = () => setOpenComments(false)
  const commentsCount = resolveGrowthToolCommentsTotal(growthTool?.counters.comments ?? 0)

  useEffect(() => {
    setFetchSettled(false)
  }, [cacheKey])

  useEffect(() => {
    if (!isLoading && !isFetching) {
      setFetchSettled(true)
    }
  }, [isLoading, isFetching, cacheKey])

  useEffect(() => {
    if (!growthTool?.id) return
    void trackGrowthToolView({ growthToolId: growthTool.id, cacheKey })
  }, [growthTool?.id, cacheKey, trackGrowthToolView])

  if (!fetchSettled || isLoading || isFetching) {
    return (
      <Stack alignItems="center" justifyContent="center" height="100%">
        <CircularProgress />
      </Stack>
    )
  }

  if (isAccessRestricted) {
    return <GrowthToolAccessRestrictedStub message={extractApiErrorMessage(growthToolError)} />
  }

  if (isError || !growthTool) return <NotFound text={'Инструмент личностного роста не найден'} />

  return (
    <>
      <Stack direction="row" sx={StackStyle}>
        <Stack direction="column" sx={MainBox}>
          <GrowthToolDescription growthTool={growthTool} />
          {!isTablet && (
            <CommentsWrapper
              growthToolId={growthTool.id}
              cacheKey={cacheKey}
              growthTool={growthTool}
            />
          )}
          {isTablet && (
            <Button
              variant="action"
              disableRipple
              startIcon={<IconComments fontSize="small" />}
              onClick={handleOpenComments}
              sx={[CommentsButton, isPwaMobile && commentsButtonPwaSx]}
            >
              Дискуссия {formatNumber(commentsCount)}
            </Button>
          )}
        </Stack>
        {!isTablet && (
          <Box sx={SideBox}>
            <GrowthToolInfo growthTool={growthTool} />
          </Box>
        )}
      </Stack>
      <CommentsDrawer open={openComments} onClose={handleCloseComments}>
        <CommentsWrapper
          growthToolId={growthTool.id}
          cacheKey={cacheKey}
          growthTool={growthTool}
          onClose={handleCloseComments}
        />
      </CommentsDrawer>
    </>
  )
}
